import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Select, Drawer } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getProductDormants } from '../../services/rapportService';
import {
  NdoePage, NdoePageHeader, NdoePeriodPicker, NdoeActionButton,
  NdoeResponsiveTable, NdoeDataCard, NdoeStatusBadge, NdoeImage,
  NdoeStats, NdoeStatCard, NdoeError,
  NdoeArticleLink, useFicheArticle,
} from '../../components/ndoe';
import { toCsv, downloadCsv } from './exportCsv';
import useRefLists from './useRefLists';
import ReportFilters from './ReportFilters';
import { money, days, dateTxt } from './format';
import './rapports.css';

// « Jamais vendu » n'est pas « 0 jour » : on ne compte pas des jours qui n'existent pas.
const sansVente = (v) => (v === null || v === undefined ? 'Jamais vendu' : `${v} j`);

const statutBadge = (row) => (
  <NdoeStatusBadge kind="custom" tone={row.statut_tone} label={row.statut_label} />
);
const actionBadge = (row) => (
  <NdoeStatusBadge kind="custom" tone={row.action_tone} label={row.action_label} />
);

const SORTS = [
  { value: 'valeur', label: 'Valeur dormante' },
  { value: 'potentiel', label: 'Vendait le mieux avant' },
  { value: 'anciennete', label: 'Dort depuis le plus longtemps' },
  { value: 'stock', label: 'Plus grand stock' },
];
const STATUTS = [
  { value: '', label: 'Tous les statuts' },
  { value: 'refroidi', label: 'Refroidi (vendait puis stop)' },
  { value: 'dormant', label: 'Dormant' },
  { value: 'jamais_vendu', label: 'Jamais vendu' },
];
const ACTIONS = [
  { value: '', label: 'Toutes les actions' },
  { value: 'pousser', label: 'Remettre en avant' },
  { value: 'solder', label: 'À solder' },
  { value: 'tester', label: 'À tester' },
  { value: 'surveiller', label: 'Surveiller' },
  { value: 'avoid', label: 'Ne pas remettre en avant' },
];

const EMPTY_FILTERS = { marque: '', categorie: '', matiere: '', couleur: '', pointure: '', statut: '', action: '' };

export default function ProduitsDormants() {
  const user = useSelector((s) => s.user?.currentUser);
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';

  const [period, setPeriod] = useState({ period: 90 });   // horizon de dormance par défaut
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('valeur');
  const [page, setPage] = useState(1);

  const [payload, setPayload] = useState({ meta: {}, summary: {}, data: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [detail, setDetail] = useState(null);
  const { openFiche, ficheElement } = useFicheArticle();

  const refs = useRefLists(isAdmin);

  useEffect(() => {
    const timer = setTimeout(() => { setSearch(q.trim()); setPage(1); }, 400);
    return () => clearTimeout(timer);
  }, [q]);

  const apiParams = useMemo(() => ({
    period: period.period === 'custom' ? undefined : period.period,
    start_date: period.period === 'custom' ? period.start_date : undefined,
    end_date: period.period === 'custom' ? period.end_date : undefined,
    ...filters, q: search, sort,
  }), [period, filters, search, sort]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getProductDormants({ ...apiParams, page, page_size: 20 });
      setPayload(data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Impossible de charger le rapport des produits dormants.');
    } finally { setLoading(false); }
  }, [apiParams, page]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);
  useEffect(() => { setPage(1); }, [period, filters, sort]);

  const setFilter = (key) => (value) => setFilters((f) => ({ ...f, [key]: value ?? '' }));
  const activeCount = Object.values(filters).filter(Boolean).length + (search ? 1 : 0);

  const handleExport = async () => {
    setExporting(true);
    try {
      const all = [];
      let current = 1;
      let total = Infinity;
      while (all.length < total && current <= 25) {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await getProductDormants({ ...apiParams, page: current, page_size: 60 });
        total = data.meta?.total ?? all.length;
        all.push(...(data.data || []));
        if (!data.data?.length) break;
        current += 1;
      }
      const columns = [
        { title: 'Produit', value: (r) => r.nom_produit },
        { title: 'Marque', value: (r) => r.marque },
        { title: 'Catégorie', value: (r) => r.categorie },
        { title: 'Matière', value: (r) => r.matiere },
        { title: 'Pointures bloquées', value: (r) => r.pointures_bloquees.map((t) => `P${t}`).join(' ') },
        { title: 'Stock immobilisé', value: (r) => r.stock_actuel },
        { title: 'Valeur au prix de vente', value: (r) => r.valeur_stock },
        { title: 'Jours sans vente', value: (r) => (r.jours_sans_vente === null ? 'jamais vendu' : r.jours_sans_vente) },
        { title: 'Vendu au total', value: (r) => r.vendu_total },
        { title: 'Vendait avant (fenêtre précédente)', value: (r) => r.ventes_avant },
        { title: 'Âge du stock restant (j)', value: (r) => r.jours_en_stock ?? '' },
        { title: 'Âge au catalogue (j)', value: (r) => r.age_catalogue ?? '' },
        { title: 'Dernière vente', value: (r) => dateTxt(r.derniere_vente) },
        { title: 'Dernière entrée', value: (r) => dateTxt(r.derniere_entree) },
        { title: 'Statut', value: (r) => r.statut_label },
        { title: 'Action', value: (r) => r.action_label },
        { title: 'Raison', value: (r) => r.raison },
      ];
      const { start_date: from, end_date: to } = payload.meta || {};
      downloadCsv(`produits-dormants_${from || ''}_${to || ''}.csv`, toCsv(columns, all));
      toast.success(`${all.length} produit(s) exporté(s).`);
    } catch (e) {
      toast.error("L'export a échoué.");
    } finally { setExporting(false); }
  };

  if (!isAdmin) {
    return (
      <NdoePage>
        <NdoeError title="Accès réservé" text="Le module Rapports est réservé à l'administrateur." />
      </NdoePage>
    );
  }

  const { summary = {}, meta = {}, data = [] } = payload;
  const horizon = meta.horizon ?? meta.days ?? 90;

  const columns = [
    {
      title: 'Photo', dataIndex: 'img', width: 76,
      render: (img, r) => <NdoeImage src={img} alt={r.nom_produit} size="md" preview={false} />,
    },
    {
      title: 'Produit', dataIndex: 'nom_produit',
      render: (nom, r) => (
        <div className="pp-produit">
          <b>{nom || '—'}</b>
          <small>{[r.marque, r.categorie, r.matiere].filter(Boolean).join(' · ') || '—'}</small>
          {r.pointures_bloquees.length > 0 && (
            <div className="pp-pointures">
              {r.pointures_bloquees.slice(0, 6).map((t) => <span className="pp-pt pp-pt--cold" key={t}>P{t}</span>)}
              {r.pointures_bloquees.length > 6 && <span className="pp-pt pp-pt--cold">+{r.pointures_bloquees.length - 6}</span>}
            </div>
          )}
          {r.deconseille && <NdoeStatusBadge kind="custom" tone="danger" label="Déconseillé" />}
        </div>
      ),
    },
    { title: 'Stock', dataIndex: 'stock_actuel', align: 'right', width: 80, render: (v) => <span className="num">{v}</span> },
    {
      title: 'Valeur dormante', dataIndex: 'valeur_stock', align: 'right', width: 128,
      render: (v) => <span className="num dorm-valeur">{money(v)}</span>,
    },
    {
      title: 'Sans vente depuis', dataIndex: 'jours_sans_vente', align: 'right', width: 132,
      render: (v) => <span className="num">{sansVente(v)}</span>,
    },
    {
      title: 'Vendait avant', dataIndex: 'ventes_avant', align: 'right', width: 116,
      render: (v) => <span className="num">{v > 0 ? `${v} paire(s)` : '—'}</span>,
    },
    { title: 'Âge du stock', dataIndex: 'jours_en_stock', align: 'right', width: 110, render: (v) => <span className="num">{days(v)}</span> },
    { title: 'Statut', dataIndex: 'statut', width: 140, render: (_, r) => statutBadge(r) },
    { title: 'Action', dataIndex: 'action', width: 176, render: (_, r) => actionBadge(r) },
  ];

  const renderCard = (r) => (
    <NdoeDataCard
      key={r.id_produit}
      media={<NdoeImage src={r.img} alt={r.nom_produit} size="lg" preview={false} />}
      title={r.nom_produit || '—'}
      subtitle={[r.marque, r.categorie].filter(Boolean).join(' · ')}
      badge={actionBadge(r)}
      metrics={[
        { label: 'Stock', value: r.stock_actuel },
        { label: 'Valeur dormante', value: money(r.valeur_stock) },
        { label: 'Sans vente', value: sansVente(r.jours_sans_vente) },
        { label: 'Vendait avant', value: r.ventes_avant > 0 ? `${r.ventes_avant} paire(s)` : '—' },
      ]}
      onClick={() => setDetail(r)}
    />
  );

  const KpiCard = ({ label, value, gold, onClick }) => (
    onClick ? (
      <div className="pp-kpi-click" role="button" tabIndex={0} onClick={onClick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}>
        <NdoeStatCard label={label} value={value} gold={gold} />
      </div>
    ) : <NdoeStatCard label={label} value={value} gold={gold} />
  );

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Rapports"
        title="Produits dormants"
        subtitle="Du stock, mais plus de ventes — ce qu'il faut remettre en avant, tester, ou solder."
        actions={
          <NdoeActionButton variant="secondary" icon={<DownloadOutlined />} loading={exporting} onClick={handleExport}>
            Exporter CSV
          </NdoeActionButton>
        }
      />

      <div className="pp-period">
        <NdoePeriodPicker value={period} onChange={setPeriod} />
        <span className="pp-range">aucune vente depuis <b>{horizon} jours</b></span>
      </div>

      <NdoeStats className="pp-stats">
        <KpiCard label="Produits dormants" value={summary.produits ?? 0} />
        <KpiCard label="Paires immobilisées" value={summary.paires_immobilisees ?? 0} />
        <KpiCard label="Valeur dormante (prix de vente)" value={money(summary.valeur_immobilisee)} />
        <KpiCard
          label={summary.valorisation_couverture_pct
            ? `Capital au coût (${summary.valorisation_couverture_pct} % valorisé)`
            : 'Capital immobilisé (au coût)'}
          value={summary.valorisation_couverture_pct ? money(summary.valeur_immobilisee_cout) : '—'}
        />
        <KpiCard label="À remettre en avant" value={summary.a_remettre_en_avant ?? 0} gold
          onClick={() => setFilter('action')('pousser')} />
        <KpiCard label="À solder" value={summary.a_solder ?? 0}
          onClick={() => setFilter('action')('solder')} />
        <KpiCard label="Jamais vendus" value={summary.jamais_vendus ?? 0}
          onClick={() => setFilter('statut')('jamais_vendu')} />
      </NdoeStats>

      <p className="pp-note rp-note">
        <NdoeStatusBadge kind="custom" tone="info" label="Lecture" />
        {' '}La période choisie <b>est l'horizon de dormance</b>&nbsp;: on liste ce qui a du stock et
        {' '}<b>aucune vente depuis {horizon} jours</b>. La <b>valeur dormante</b> est calculée au <b>prix de vente</b>
        {' '}(chiffre d'affaires en sommeil), pas au coût d'achat.
        {summary.trop_recents > 0 && (
          <> {summary.trop_recents} produit(s) trop récemment entrés au catalogue ont été écartés&nbsp;: ils n'ont pas encore eu leur chance.</>
        )}
      </p>

      <ReportFilters
        q={q} setQ={setQ}
        filters={filters} setFilter={setFilter}
        refs={refs}
        sort={sort} setSort={setSort} sorts={SORTS}
        activeCount={activeCount}
      >
        <div className="ndoe-filter-bar__field">
          <Select placeholder="Statut" style={{ minWidth: 200 }} options={STATUTS}
            value={filters.statut} onChange={setFilter('statut')} />
        </div>
        <div className="ndoe-filter-bar__field">
          <Select placeholder="Action" style={{ minWidth: 190 }} options={ACTIONS}
            value={filters.action} onChange={setFilter('action')} />
        </div>
      </ReportFilters>

      {error && !loading ? (
        <NdoeError text={error} onRetry={load} />
      ) : (
        <NdoeResponsiveTable
          className="pp-table"
          data={data}
          columns={columns}
          renderCard={renderCard}
          rowKey="id_produit"
          loading={loading}
          error={error}
          onRetry={load}
          scroll={{ x: 1240 }}
          onRow={(record) => ({ onClick: () => setDetail(record), style: { cursor: 'pointer' } })}
          pagination={{ current: meta.page || 1, pageSize: meta.page_size || 20, total: meta.total || 0, showSizeChanger: false }}
          onChange={(pagination) => setPage(pagination?.current || 1)}
          empty={{
            icon: '🌱',
            title: 'Aucun produit dormant sur cet horizon',
            text: 'Tout ce qui a du stock a vendu au moins une fois. Élargis l\'horizon pour voir plus loin.',
          }}
        />
      )}

      <Drawer
        open={!!detail}
        onClose={() => setDetail(null)}
        width={560}
        title={detail ? <NdoeArticleLink onClick={() => openFiche(detail.id_produit)}>{detail.nom_produit}</NdoeArticleLink> : ('' || 'Détail produit dormant')}
        className="pp-drawer"
      >
        {detail && (
          <div className="pp-detail">
            <div className="pp-detail__head">
              <NdoeImage src={detail.img} alt={detail.nom_produit} size="hero" />
              <div className="pp-detail__meta">
                <b>{[detail.marque, detail.categorie].filter(Boolean).join(' · ') || '—'}</b>
                <small>{detail.matiere || 'Matière non renseignée'}</small>
                <small>{(detail.couleurs || []).join(', ') || 'Couleur non renseignée'}</small>
                <div className="pp-detail__badges">
                  {statutBadge(detail)}
                  {actionBadge(detail)}
                  {detail.deconseille && <NdoeStatusBadge kind="custom" tone="danger" label="Déconseillé" />}
                </div>
              </div>
            </div>

            <div className="rp-raison">
              <span className="rp-raison__label">Pourquoi cette action</span>
              <p>{detail.raison}</p>
            </div>

            <NdoeStats>
              <NdoeStatCard label="Valeur dormante" value={money(detail.valeur_stock)} gold />
              <NdoeStatCard label="Paires immobilisées" value={detail.stock_actuel} />
              <NdoeStatCard label="Sans vente depuis" value={sansVente(detail.jours_sans_vente)} />
              <NdoeStatCard label="Vendait avant" value={detail.ventes_avant > 0 ? `${detail.ventes_avant} paire(s)` : '—'} />
              <NdoeStatCard label="Vendu au total" value={detail.vendu_total} />
              <NdoeStatCard label="Âge du stock" value={days(detail.jours_en_stock)} />
            </NdoeStats>

            <h4 className="pp-detail__title">Pointures bloquées</h4>
            <div className="pp-detail__pt rp-pt">
              <div className="pp-detail__pt-row rp-pt-row pp-detail__pt-row--head">
                <span>Pointure</span><span>Stock</span><span>Dernière vente</span><span>Vendu (horizon)</span>
              </div>
              {detail.pointures.filter((p) => p.stock > 0).map((p) => (
                <div className="pp-detail__pt-row rp-pt-row" key={p.taille}>
                  <span>P{p.taille}</span>
                  <span className="num">{p.stock}</span>
                  <span className="num">{p.derniere_vente ? dateTxt(p.derniere_vente) : 'jamais'}</span>
                  <span className="num">{p.vendu_fenetre}</span>
                </div>
              ))}
              {!detail.pointures.some((p) => p.stock > 0) && <p className="pp-detail__warn">Aucune pointure en stock.</p>}
            </div>

            <h4 className="pp-detail__title">Chronologie</h4>
            <ul className="pp-detail__dates">
              <li><span>Dernière vente</span><b>{detail.derniere_vente ? dateTxt(detail.derniere_vente) : 'Jamais vendu'}</b></li>
              <li><span>Dernière entrée en stock</span><b>{dateTxt(detail.derniere_entree)}</b></li>
              <li><span>Âge du stock restant</span><b>{days(detail.jours_en_stock)}</b></li>
              <li><span>Ancienneté au catalogue</span><b>{days(detail.age_catalogue)}</b></li>
            </ul>

            {detail.deconseille && detail.motif_deconseille && (
              <p className="pp-detail__motif">Motif du déconseil&nbsp;: {detail.motif_deconseille}</p>
            )}
          </div>
        )}
      </Drawer>
      {ficheElement}
    </NdoePage>
  );
}
